import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { Paper, Typography, Box } from '@mui/material';
import sortBy from 'lodash/sortBy';

import { Event } from 'modules/events/types/eventsTypes';
import { Loader } from 'ui';
import { LinkButton } from 'ui/Buttons';
import { addNotificationAction } from 'store/notifications/notificationsActions';
import { EventsSelectionList } from 'modules/events/components/EventsSelectionList';
import { getEvents } from '../selectors/eventsSelectors';
import { fetchEventsAction } from '../store/eventsActions';
import { activateEvent, deactivateEvent } from '../services/eventsService';

export const EventPanelView = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const events = useSelector(getEvents);
  const [selectedEvents, setSelectedEvents] = useState<Event[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showActive, setShowActive] = useState(true);
  const [hiddenActions, setHiddenActions] = useState<string[]>([]);

  const actions = [
    {
      name: t('Duplicate'),
      action: () => {
        setIsLoading(true);

        // eslint-disable-next-line no-alert
        alert('This action is not ready yet');

        setIsLoading(false);
      },
    },
    {
      name: t('Delete'),
      action: () => {
        setIsLoading(true);

        // eslint-disable-next-line no-alert
        alert('This action is not ready yet');

        setIsLoading(false);
      },
    },
    {
      name: t('Activate'),
      action: () => {
        setIsLoading(true);

        Promise.all(selectedEvents.map(item => item.id!).map(activateEvent))
          .then(() => dispatch(fetchEventsAction('ALL')))
          .catch((error: any) =>
            dispatch(
              addNotificationAction({
                message: error.message,
                variant: 'error',
              }),
            ),
          )
          .finally(() => setIsLoading(false));
      },
    },
    {
      name: t('Deactivate'),
      action: () => {
        setIsLoading(true);

        Promise.all(selectedEvents.map(item => item.id!).map(deactivateEvent))
          .then(() => dispatch(fetchEventsAction('ALL')))
          .catch((error: any) =>
            dispatch(
              addNotificationAction({
                message: error.message,
                variant: 'error',
              }),
            ),
          )
          .finally(() => setIsLoading(false));
      },
    },
  ];

  const handleSelectionChange = (items: Event[]) => setSelectedEvents(items);

  useEffect(() => {
    dispatch(fetchEventsAction('ALL'));
  }, [dispatch]);

  useEffect(() => {
    const hideRules = {
      Duplicate: selectedEvents.length !== 1,
      Activate: showActive,
      Deactivate: !showActive,
    };

    setHiddenActions(
      Object.entries(hideRules)
        .map(([name, rule]) => rule && name)
        .filter(Boolean) as string[],
    );
  }, [selectedEvents, showActive]);

  return (
    <>
      <Box mb={2}>
        <Typography variant="h5">{t('Event list')}</Typography>
      </Box>

      <Paper elevation={3}>
        {isLoading || events.isPending ? (
          <Box py={4}>
            <Loader center />
          </Box>
        ) : (
          <EventsSelectionList
            actions={actions.map(item => ({ ...item, hidden: hiddenActions.includes(item.name) }))}
            events={sortBy(events.data, 'title')}
            onSelectionChange={handleSelectionChange}
            showActive={showActive}
            setShowActive={setShowActive}
            toolbar={
              // @ts-ignore
              <LinkButton
                to="/event-panel/create"
                label={t('Create event')}
                color="primary"
                variant="contained"
                size="small"
              />
            }
            columns={['title', 'eventDate', 'category', 'organiser']}
            editMode
          />
        )}
      </Paper>
    </>
  );
};
